import React, {useEffect, useState} from "react";
import _ from "lodash";
import Promise from "bluebird";
import {updateSingleBlogPost} from "../../../content/services/blog-post-data-service";
import {
    prepareContentStatistics,
    markAsDraftBlogPost, markAsReadyBlogPost,
    prepareHtmlContentDetails,
    processContentLines, addImagesToContentLines, removeSectionHeader
} from "../../../content/utils/blog-content-util";
import {SAVE_BLOG_POST_TIME_DELAY} from "../../../content/constants/blog.constant";
import {updateSingleBlogPostContent} from "../../../content/services/blog-post-content-data-service";
import GenerateContentFromPrompt from "../../../openai/components/generate-content-from-prompt";
import {PRODUCT_BLOG_POST_PROMPTS} from "../../constants/product-blog-post.constant";
import {
    prepareContentPromptText,
    prepareExpandSectionContentPromptText, prepareExtractKeyWordsAndMapLinksPromptText, prepareKeywordLinkMapping,
    prepareProductLinkDetails,
    prepareRewriteSectionContentPromptText,
    prepareRewriteSectionTitlePromptText,
    prepareTitlePromptText
} from "../../utils/product-blog-post-util";
import DeletePostContentLine from "./delete-post-content-line";
import SelectHtmlTag from "../../../content/components/rich-text-editor/select-html-tag";
import RenderFields from "../../../common/ui/render-fields";
import {COLLECTION_INPUT_FIELDS} from "../../constants/shopify-blog.constant";
import SelectShopifyBlog from "../blogs/select-shopify-blog";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import {ChevronDownIcon, ChevronRightIcon, TagIcon} from "@heroicons/react/24/solid";
import ProductBlogPostMeta from "./product-blog-post-meta";
import ProductBlogPostTitles from "./product-blog-post-titles";
import ProductBlogPostPreviewMode from "./product-blog-post-preview-mode";
import AddEditProductImage from "../shopify-blog/add-edit-product-image";
import {prepareGenericJSONPromptMessages} from "../../../design/utils/open-ai-prompt-util";
import {generateContent} from "../../../common/services/open-ai-service";
import DeleteItem from "../../../common/ui/delete-item";
import ProductBlogPostInstructions from "./product-blog-post-instructions";
import ProductBlogPostStatistics from "./product-blog-post-statistics";

const ProductBlogPostWriter = ({
                                   blogPost,
                                   setBlogPost,
                                   blogPostContent,
                                   setBlogPostContent,
                                   contentTags = '',
                                   productsList,
                                   shopifyBlogsList,
                                   collectionsList,
                                   setSaveInProgress,
                                   selectedProductImageList,
                                   setSelectedProductImageList
                               }) => {
    const [initialStep, setInitialStep] = useState(true);
    const [contentLines, setContentLines] = useState(null);
    let [changesCount, setChangesCount] = useState(0);
    const [mainContentPrompt, setMainContentPrompt] = useState(null);
    const [mainContent, setMainContent] = useState(null);
    const [titlePrompt, setTitlePrompt] = useState(null);
    const [titles, setTitles] = useState(null);
    const [showTitles, setShowTitles] = useState(false);
    const [statistics, setStatistics] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [apiCallInProgressFor, setApiCallInProgressFor] = useState(null);
    const [sectionTitlesMap, setSectionTitlesMap] = useState({});
    const [productLinkFields, setProductLinkFields] = useState([]);
    const [productUrls, setProductUrls] = useState(null);
    const [htmlContent, setHtmlContent] = useState(null);
    const [editDetail, setEditDetail] = useState(null);
    const [currentContentLineIndex, setCurrentContentLineIndex] = useState(null);
    const [addLinksInProgress, setAddLinksInProgress] = useState(false);
    const [expandSectionInProgress, setExpandSectionInProgress] = useState(false);

    useEffect(() => {
        if (initialStep) {
            setInitialStep(false);
            init();
        }
    }, [initialStep]);

    const init = () => {
        const existingBlogPostContent = {...blogPostContent};
        setContentLines(existingBlogPostContent?.contentLines || []);
        setStatistics(existingBlogPostContent?.statistics);
        setHtmlContent(existingBlogPostContent.htmlContent);
        setMainContent(_.join(_.map(existingBlogPostContent?.contentLines, 'text'), '\n'));

        const productBlogs = _.filter(shopifyBlogsList, (blog) => blog.type === 'Product Blog');
        const existingBlogPost = {...blogPost};

        if (_.isEmpty(existingBlogPost.shopifyBlogId) && !_.isEmpty(productBlogs)) {
            existingBlogPost.shopifyBlogId = productBlogs[0].id;
            setBlogPost(existingBlogPost);
        }

        setTitlePrompt(prepareTitlePromptText(productsList[0]?.onlineStoreUrl, blogPost?.toneOfVoice, contentTags));

        const productLinkDetails = prepareProductLinkDetails(productsList, collectionsList);
        setProductLinkFields(productLinkDetails?.productLinkFields);
        setProductUrls(productLinkDetails?.productUrls);

        const initialSectionTitlesMap = {};
        _.forEach(existingBlogPostContent?.contentLines, (contentLine) => {
            if (contentLine?.isSection) {
                initialSectionTitlesMap[contentLine.text] = true;
            }
        });

        setSectionTitlesMap(initialSectionTitlesMap);
        setEditDetail({
            contentLineIndex: -1,
            text: '',
            isOpen: false,
        });
    }

    const handleBlogPostChanges = (event) => {
        const {name, value, type} = event.target;
        const existingBlogPost = {...blogPost};

        if (type === 'checkbox') {
            existingBlogPost[name] = !existingBlogPost[name];
        } else if (type === 'number') {
            existingBlogPost[name] = _.parseInt(value);
        } else {
            existingBlogPost[name] = value;
        }

        setBlogPost(existingBlogPost);
        saveBlogPost(existingBlogPost);
    }

    const saveBlogPost = async (existingBlogPost) => {
        existingBlogPost.statistics = {...statistics};
        await updateSingleBlogPost(existingBlogPost);
    }

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {
                if (changesCount > 0) {
                    resetChangesCount();
                    saveBlogPostContent();
                }
            }, SAVE_BLOG_POST_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const saveBlogPostContent = async () => {
        setSaveInProgress(true);

        const existingBlogPostContent: any = {...blogPostContent};
        existingBlogPostContent.contentLines = [...contentLines];
        existingBlogPostContent.statistics = {...statistics};

        const htmlContentText = prepareHtmlContentDetails(existingBlogPostContent.contentLines);
        existingBlogPostContent.htmlContent = htmlContentText;
        setHtmlContent(htmlContentText);

        await updateSingleBlogPostContent(existingBlogPostContent);
        setBlogPostContent(existingBlogPostContent);
        setSaveInProgress(false);
    }

    useEffect(() => {
        if (blogPost?.title) {
            setMainContentPrompt(prepareContentPromptText(blogPost.title, productsList[0]?.onlineStoreUrl, blogPost?.toneOfVoice, contentTags));
        }
    }, [blogPost?.title]);

    const prepareContentLines = (generatedText, isExpandMainContent?, isExpandSectionContent?, contentLineIndex?) => {
        let existingContentLines = [...contentLines];

        let newContentLines: any = [];

        _.forEach(_.split(generatedText, '\n'), (line) => {
            if (!_.isEmpty(line)) {
                if (!isExpandSectionContent || (isExpandSectionContent && line !== existingContentLines[contentLineIndex].text)) {
                    newContentLines.push({
                        text: _.trim(line),
                        newType: 'p',
                        section: isExpandSectionContent ? existingContentLines[contentLineIndex].text : null
                    });
                }
            }
        });

        if (isExpandSectionContent) {
            const sectionContentLines = _.filter(existingContentLines, (line) => line.section === existingContentLines[contentLineIndex].text);
            existingContentLines.splice(contentLineIndex + 1, sectionContentLines?.length, ...newContentLines);
        } else {
            existingContentLines = newContentLines;
        }

        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const prepareMainContentExcerpts = (generatedText) => {
        const existingBlogPostContent: any = {...blogPostContent};
        generatedText = _.replace(generatedText, /"/g, '');
        const excerptsLine = {
            text: _.trim(generatedText),
            isMeta: true,
            metaType: 'excerpts',
            metaLabel: 'Excerpts',
            newType: 'p',
        }

        const tagsLine = {
            text: contentTags,
            isMeta: true,
            metaType: 'tags',
            metaLabel: 'Tags',
            newType: 'p',
        }

        existingBlogPostContent.metaLines = [excerptsLine, tagsLine];
        setBlogPostContent(existingBlogPostContent);
        incrementChangesCount();
    }

    const prepareExpandedMainContentLines = (generatedText) => {
        prepareContentLines(generatedText, true, false);
    }

    const prepareExpandedSectionContentLines = (generatedText, contentLineIndex) => {
        prepareContentLines(generatedText, false, true, contentLineIndex);
    }

    const prepareSectionTitles = (generatedText, contentLineIndex) => {
        let existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];

        const existingSectionTitlesMap = {...sectionTitlesMap};
        const previousSectionTitleInfo = existingSectionTitlesMap[existingContentLine.text];
        delete existingSectionTitlesMap[existingContentLine.text];

        const sectionContentLines = _.filter(existingContentLines, (line) => line.section === existingContentLine.text);
        let newSectionTitle = _.replace(_.trim(generatedText), /"/g, '');

        if (newSectionTitle?.indexOf('Section Title:') > -1) {
            newSectionTitle = _.split(newSectionTitle, 'Section Title:')[1];
        }

        existingContentLine.text = newSectionTitle;

        _.forEach(sectionContentLines, (contentLine) => {
            contentLine.section = newSectionTitle;
        });

        existingContentLines.splice(contentLineIndex, 1, existingContentLine);
        existingContentLines.splice(contentLineIndex + 1, sectionContentLines?.length, ...sectionContentLines);
        setContentLines(existingContentLines);

        existingSectionTitlesMap[newSectionTitle] = previousSectionTitleInfo;
        setSectionTitlesMap(existingSectionTitlesMap);

        prepareStatistics(existingContentLines);
    }

    const prepareRewrittenContentLine = (generatedText, contentLineIndex) => {
        let existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];
        let newContentLines: any = [];

        _.forEach(_.split(generatedText, '\n'), (line) => {
            if (!_.isEmpty(line)) {
                newContentLines.push({
                    text: _.trim(line),
                    newType: 'p',
                    section: existingContentLine.section
                });
            }
        });

        if (_.isEmpty(newContentLines)) {
            existingContentLines[contentLineIndex].text = _.trim(generatedText);
        } else {
            existingContentLines.splice(contentLineIndex, newContentLines?.length, ...newContentLines);
        }

        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const prepareTitles = (generatedText) => {
        let titlesText = _.replace(generatedText, /"/g, '');
        const titlesList = _.split(titlesText, '\n');

        titlesList.forEach((title, index) => {
            if (title) {
                const titleWords = _.split(title, ' ');
                const firstWord = titleWords[0];

                if (_.isNumber(_.toNumber(firstWord))) {
                    titlesList[index] = _.trim(_.replace(title, firstWord, ''));
                }
            }
        });

        setTitles(titlesList);
        setShowTitles(true);
    }

    const handleTitleSelection = (selectedTitle) => {
        const event = {
            target: {
                name: 'title',
                value: selectedTitle,
                type: 'text'
            }
        }

        handleBlogPostChanges(event);
        setShowTitles(false);
    }

    const prepareExpandSectionContentPrompt = (contentLine) => {
        const existingContentLines = [...contentLines];
        const sectionContentLines = _.filter(existingContentLines, (line) => line.section === contentLine.text);
        let sectionContentText = contentLine.text + '\n';
        sectionContentText += _.join(_.map(sectionContentLines, 'text'), '\n');

        return prepareExpandSectionContentPromptText(sectionContentText);
    }

    const prepareRewriteSectionTitlePrompt = (contentLine) => {
        const existingContentLines = [...contentLines];
        const sectionContentLines = _.filter(existingContentLines, (line) => line.section === contentLine.text);
        let sectionContentText = _.join(_.map(sectionContentLines, 'text'), '\n');

        return prepareRewriteSectionTitlePromptText(contentLine.text, sectionContentText);
    }

    const prepareRewriteSectionContentPrompt = (contentLine) => {
        return prepareRewriteSectionContentPromptText(contentLine.text);
    }

    const prepareExtractKeyWordsAndMapLinksPrompt = (contentLine) => {
        return prepareExtractKeyWordsAndMapLinksPromptText(contentLine.text, productUrls);
    }

    const handleRemoveContentLine = (contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines.splice(contentLineIndex, 1);
        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const prepareStatistics = (existingContentLines) => {
        const generatedText = _.join(_.map(existingContentLines, 'text'), ' ');
        const newStatistics = prepareContentStatistics(existingContentLines, generatedText);

        setMainContent(generatedText);
        setStatistics(newStatistics);
        incrementChangesCount();
    }

    const changeContentLineType = (contentLineIndex, newType) => {
        const existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];
        existingContentLine.newType = newType;

        if (newType === 'h2') {
            existingContentLine.text = removeSectionHeader(existingContentLine.text);
            const existingSectionTitlesMap = {...sectionTitlesMap};
            existingSectionTitlesMap[existingContentLine.text] = true;
            setSectionTitlesMap(existingSectionTitlesMap);
        }

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const handleShowHideSectionContent = (contentLine) => {
        const existingSectionTitlesMap = {...sectionTitlesMap};
        existingSectionTitlesMap[contentLine.text] = !existingSectionTitlesMap[contentLine.text];
        setSectionTitlesMap(existingSectionTitlesMap);
    }

    const addLinksToContentLine = (links, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines[contentLineIndex].links = links;

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const prepareContentLinks = (generatedText, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        const mappingLinks = prepareKeywordLinkMapping(generatedText, productLinkFields, existingContentLines[contentLineIndex]?.text);
        addLinksToContentLine(mappingLinks, contentLineIndex);
    }

    const addImageToContentLine = (productImage, contentLineIndex) => {
        const imageLine = {
            key: "P",
            type: "p",
            text: productImage.productTitle,
            html: `<a href="${productImage?.onlineStoreUrl}" target="_blank"><img src="${productImage?.url}" alt="${productImage.productTitle}"></a>`,
            newType: "br",
            isImage: true,
            productImageId: productImage.id,
            url: productImage?.url
        }

        const existingContentLines = [...contentLines];
        existingContentLines.splice(contentLineIndex + 1, 0, imageLine);

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);
        incrementChangesCount();
    }

    const addMultipleImagesToContent = () => {
        const existingImages = [...selectedProductImageList];
        const existingContentLines = [...contentLines];

        const newContentLines = addImagesToContentLines(existingImages, existingContentLines);

        processContentLines(newContentLines);
        setContentLines(newContentLines);
        prepareStatistics(newContentLines);
    }

    const prepareHtmlContent = () => {
        const existingContentLines = [...contentLines];
        const htmlContentText = prepareHtmlContentDetails(existingContentLines);
        setHtmlContent(htmlContentText);
    }

    const triggerExpandSectionContentProcess = async () => {
        setExpandSectionInProgress(true);
        let existingContentLines = [...contentLines];
        let promptContent, promptTitle;
        const newContentLines = [];

        let currentSectionIndex = 0;

        await Promise.mapSeries(existingContentLines, async (contentLine, contentLineIndex) => {

            if (contentLine.isSection) {
                setCurrentContentLineIndex(++currentSectionIndex);
                newContentLines.push(contentLine);

                promptContent = prepareExpandSectionContentPrompt(contentLine);
                promptTitle = PRODUCT_BLOG_POST_PROMPTS.EXPAND_CONTENT.title;

                const generatedText: any = await initiateChatApiCall(promptTitle, promptContent, contentLine, contentLineIndex);

                _.forEach(_.split(generatedText, '\n'), (line) => {
                    if (!_.isEmpty(line)) {
                        newContentLines.push({
                            text: _.trim(line),
                            newType: 'p',
                            section: contentLine.text,
                        });
                    }
                });

                setApiCallInProgressFor(null);
            } else if (_.isEmpty(contentLine.section)) {
                newContentLines.push(contentLine);
            }
        });

        setContentLines(newContentLines);
        prepareStatistics(newContentLines);
        setExpandSectionInProgress(false);
    }

    const triggerContentLinkMappingProcess = async (sectionHeaderContentLine?) => {
        setAddLinksInProgress(true);
        let existingContentLines = [...contentLines];
        let promptContent, promptTitle;

        await Promise.mapSeries(existingContentLines, async (contentLine, contentLineIndex) => {
            setCurrentContentLineIndex(contentLineIndex);
            if (!sectionHeaderContentLine || (sectionHeaderContentLine && contentLine.section === sectionHeaderContentLine.text)) {
                if (!contentLine.isSection && !contentLine.isImage) {
                    promptContent = prepareExtractKeyWordsAndMapLinksPrompt(contentLine);
                    promptTitle = PRODUCT_BLOG_POST_PROMPTS.EXTRACT_KEYWORDS_AND_MAP_LINKS.title;
                    const generatedText = await initiateChatApiCall(promptTitle, promptContent, contentLine, contentLineIndex);

                    existingContentLines[contentLineIndex].links = prepareKeywordLinkMapping(generatedText, productLinkFields, contentLine?.text);
                    setContentLines(existingContentLines);
                    setApiCallInProgressFor(null);
                }
            }

            if (contentLine.isSection) {
                incrementChangesCount();
            }

        });

        incrementChangesCount();
        setAddLinksInProgress(false);
    }

    const initiateChatApiCall = async (promptTitle, promptContent, contentLine, contentLineIndex) => {
        setApiCallInProgressFor(contentLine?.text);
        const promptMessages = prepareGenericJSONPromptMessages(promptTitle, promptContent);
        let generatedContent: any = await generateContent(promptMessages);

        return generatedContent?.content;
    }

    const handleMarkAsDraftBlogPost = async () => {
        setSaveInProgress(true);
        const existingBlogPost = {...blogPost};

        await markAsDraftBlogPost(existingBlogPost);

        setBlogPost(existingBlogPost);
        setSaveInProgress(false);
    }

    const handleMarkAsReadyBlogPost = async () => {
        setSaveInProgress(true);
        const existingBlogPost = {...blogPost};

        await markAsReadyBlogPost(existingBlogPost);

        setBlogPost(existingBlogPost);
        setSaveInProgress(false);
    }

    const openEditDetailInfo = (contentLine, contentLineIndex) => {
        const newEditDetail = {isOpen: true, text: contentLine.text, contentLineIndex: contentLineIndex};
        setEditDetail(newEditDetail);
    }

    const confirmEditDetailInfo = () => {
        const existingEditDetail = {...editDetail};
        const existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[existingEditDetail.contentLineIndex];

        if (existingContentLine.isSection) {
            prepareSectionTitles(existingEditDetail.text, existingEditDetail.contentLineIndex);
        } else {
            existingContentLine.text = existingEditDetail.text;
            existingContentLines[existingEditDetail.contentLineIndex] = existingContentLine;
            setContentLines(existingContentLines);
            incrementChangesCount();
        }

        cancelEditDetailInfo();
    }

    const cancelEditDetailInfo = () => {
        setEditDetail({isOpen: false, text: '', contentLineIndex: null});
    }

    const handleEditDetailChanges = (event) => {
        const {name, value, type} = event.target;
        const existingEditDetail = {...editDetail};
        existingEditDetail[name] = value;
        setEditDetail(existingEditDetail);
    }

    const handleDeleteLink = (linkIndex, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines[contentLineIndex].links.splice(linkIndex, 1);
        setContentLines(existingContentLines);
        incrementChangesCount();
    }

    return (
        <>
            <div className={'w-full items-start'}>
                <div className={'p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <div className={'flex justify-start font-semibold text-xl w-3/5'}>
                            {blogPost?.title &&
                                <h2 className={'text-purple-500'}>{blogPost?.title}</h2>
                            }

                            {!blogPost?.title &&
                                <h2 className={'text-gray-400'}>Title</h2>
                            }
                        </div>

                        {/*<div className="flex justify-center w-2/5">
                            <div className={'mr-4 items-center text-center'}>
                                <div className={'font-semibold'}>{statistics?.words || 0}</div>
                                <div>Words</div>
                            </div>
                            <div className={'items-center text-center ml-2'}>
                                <div className={'font-semibold'}>{statistics?.characters || 0}</div>
                                <div>Characters</div>
                            </div>
                            <div className={'mr-4 items-center text-center ml-2'}>
                                <div className={'font-semibold'}>{statistics?.totalSections || 0}</div>
                                <div>Sections</div>
                            </div>
                            <div className={'items-center text-center ml-2'}>
                                <div className={'font-semibold'}>{statistics?.totalParagraphs || 0}</div>
                                <div>Paragraphs</div>
                            </div>
                            <div className={'items-center text-center ml-2'}>
                                <div
                                    className={'font-semibold'}>{statistics?.totalImages || 0} / {statistics?.requiredImagesCount || 0}</div>
                                <div>Images</div>
                            </div>
                        </div>*/}

                        <div className="flex justify-end w-2/5">
                            <SelectShopifyBlog
                                selectLabel={'Shopify Blog'}
                                noFieldLabel={true}
                                fieldClassName={'w-1/2'}
                                shopifyBlogsList={shopifyBlogsList}
                                handleDetailChanges={handleBlogPostChanges}
                                parentForm={blogPost}
                                attribute={'shopifyBlogId'}/>

                            <div className={'flex'}>
                                <button
                                    className="button-outline-green px-3 py-1 m-1"
                                    onClick={() => handleMarkAsDraftBlogPost()}>
                                    Mark as Draft
                                </button>

                                <button
                                    className="button-outline-green px-3 py-1 m-1"
                                    onClick={() => handleMarkAsReadyBlogPost()}>
                                    Mark as Ready
                                </button>
                            </div>

                        </div>

                    </div>

                    <div className={'w-full flex justify-start items-center'}>
                        <div className={'w-1/6 mr-6'}>
                            <RenderFields
                                parentForm={blogPost}
                                handleDetailChanges={handleBlogPostChanges}
                                fieldList={_.groupBy(COLLECTION_INPUT_FIELDS, 'group')['meta']}
                                enableEdit={true}
                                noFieldLabel={true}
                                fieldClassName={'w-full'}/>
                        </div>

                        <GenerateContentFromPrompt
                            label={'Titles'}
                            noIcon={true}
                            showCopyButton={false}
                            sizeClass={'p-1'}
                            afterContentGenerated={prepareTitles}
                            setApiCallInProgressFor={setApiCallInProgressFor}
                            triggeredFor={'MAIN_CONTENT'}
                            promptTitle={PRODUCT_BLOG_POST_PROMPTS.TITLE.title}
                            promptContent={titlePrompt}/>

                        <GenerateContentFromPrompt
                            label={'Main Content'}
                            noIcon={true}
                            showCopyButton={false}
                            sizeClass={'p-1'}
                            afterContentGenerated={prepareContentLines}
                            setApiCallInProgressFor={setApiCallInProgressFor}
                            triggeredFor={'MAIN_CONTENT'}
                            promptTitle={PRODUCT_BLOG_POST_PROMPTS.BLOG_CONTENT.title}
                            promptContent={mainContentPrompt}/>

                        {mainContent &&
                            <>
                                <GenerateContentFromPrompt
                                    label={'Expand Main Content'}
                                    noIcon={true}
                                    showCopyButton={false}
                                    sizeClass={'p-1'}
                                    afterContentGenerated={prepareExpandedMainContentLines}
                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                    triggeredFor={'MAIN_CONTENT'}
                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.EXPAND_BLOG_CONTENT.title}
                                    promptContent={mainContentPrompt}
                                    previousResponse={mainContent}
                                    newPrompt={PRODUCT_BLOG_POST_PROMPTS.EXPAND_BLOG_CONTENT.prompt}/>

                                <button
                                    onClick={() => {
                                        triggerExpandSectionContentProcess();
                                    }}
                                    className={`m-1 px-2 py-1 button-purple-outline`}>
                                    Expand Sections
                                </button>

                                <button
                                    onClick={() => {
                                        triggerContentLinkMappingProcess();
                                    }}
                                    className={`m-1 px-2 py-1 button-purple-outline`}>
                                    Add Links
                                </button>

                                <AddEditProductImage
                                    afterProductImageSelected={() => {
                                        addMultipleImagesToContent();
                                    }}
                                    cssClass={'px-2 py-1 m-1'}
                                    isMultiple={true}
                                    statistics={statistics}
                                    selectedProductImageList={selectedProductImageList}
                                    setSelectedProductImageList={setSelectedProductImageList}
                                    productsList={productsList}/>

                                <GenerateContentFromPrompt
                                    label={'Excerpts'}
                                    noIcon={true}
                                    showCopyButton={false}
                                    sizeClass={'p-1'}
                                    afterContentGenerated={prepareMainContentExcerpts}
                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                    triggeredFor={'MAIN_CONTENT'}
                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.BLOG_CONTENT.title}
                                    promptContent={mainContentPrompt}
                                    previousResponse={mainContent}
                                    newPrompt={PRODUCT_BLOG_POST_PROMPTS.CONTENT_EXCERPTS.prompt}/>
                            </>
                        }

                        <div className={'ml-6'}>
                            <button
                                className={`${previewMode ? 'border-gray-600 text-gray-600 bg-gray-100' : 'border-green-600 text-green-100 bg-green-600 border'} border-l border-y rounded-l px-2 py-1 text-sm`}
                                type="button"
                                onClick={() => setPreviewMode(false)}>
                                Edit Mode
                            </button>
                            <button
                                className={`${previewMode ? 'border-green-600 text-green-100 bg-green-600 border' : 'border-gray-600 text-gray-600 bg-gray-100'} border-r border-y rounded-r px-2 py-1 text-sm`}
                                type="button"
                                onClick={() => setPreviewMode(true)}>
                                Preview Mode
                            </button>
                        </div>

                        <div className={'ml-6 flex justify-center items-center'}>
                            <ShowProgressSpinner
                                sizeClass="w-6 h-6"
                                dataFetchInProgress={!_.isEmpty(apiCallInProgressFor)}/>

                            {addLinksInProgress && currentContentLineIndex > -1 && contentLines?.length - 1 !== currentContentLineIndex &&
                                <span
                                    className={'ml-2'}>{currentContentLineIndex + 1} / {contentLines?.length}</span>
                            }

                            {expandSectionInProgress && currentContentLineIndex > 0 &&
                                <span
                                    className={'ml-2'}>{currentContentLineIndex} / {statistics?.totalSections}</span>
                            }
                        </div>
                    </div>

                </div>

                <div className={'w-full px-4 flex justify-between items-start'}>
                    <div
                        className={`border border-gray-400 ${previewMode ? 'bg-white' : 'bg-gray-400'} product-blog-post-content-container overflow-y-scroll px-4 pt-2 pb-16 shadow-xl rounded-lg w-2/3`}>

                        <ProductBlogPostTitles
                            titles={titles}
                            showTitles={showTitles}
                            handleTitleSelection={handleTitleSelection}/>

                        {previewMode && !_.isEmpty(contentLines) &&
                            <ProductBlogPostPreviewMode
                                htmlContent={htmlContent}
                                contentLines={contentLines}/>
                        }

                        {!previewMode && !_.isEmpty(contentLines) &&

                            <>
                                {contentLines?.map((contentLine, contentLineIndex) => (
                                    <div key={`contentLine-${contentLineIndex}`}
                                         className={'flex justify-start'}>
                                        {((contentLine.isSection || !contentLine.section) || (contentLine.section && sectionTitlesMap[contentLine.section])) &&

                                            <>
                                                <div
                                                    className="flex justify-center self-center text-2xl text-white w-10">{contentLineIndex + 1}</div>

                                                <div
                                                    className={`w-full m-2 pl-4 pr-3 py-3 border border-gray-400 shadow-lg rounded-xl
                                                    ${_.isEmpty(contentLine.section) ? '' : 'ml-16'}
                                                    ${editDetail?.isOpen && editDetail?.contentLineIndex === contentLineIndex ? 'bg-sky-200' : 'bg-white'}`}>
                                                    <div className={'flex items-center justify-between'}>
                                                        {contentLine.isSection &&
                                                            <div className={'mr-4'}>
                                                                <button
                                                                    className="button-pink-outline px-2 py-1 shadow-lg"
                                                                    onClick={() => handleShowHideSectionContent(contentLine)}>
                                                                    {sectionTitlesMap[contentLine.text] ?
                                                                        <ChevronDownIcon className="h-12 w-6"/> :
                                                                        <ChevronRightIcon className="h-12 w-6"/>
                                                                    }
                                                                </button>
                                                            </div>
                                                        }

                                                        <div className={`w-full`}>
                                                            <div
                                                                className={`flex justify-between items-center
                                                                ${contentLine.newType === 'h3' ? 'font-bold text-sky-500' : ''}
                                                                ${contentLine.isSection ? 'font-bold text-pink-500' : ''}
                                                                ${contentLine.isImage ? 'font-bold text-purple-500' : ''}`}>

                                                                {contentLine.isImage &&
                                                                    <div
                                                                        className={`flex justify-start font-semibold text-purple-500`}>
                                                                        <TagIcon className="h-6 w-6 mr-2"/>
                                                                        {contentLine?.text}
                                                                    </div>
                                                                }

                                                                {!contentLine.isImage &&
                                                                    (!editDetail?.isOpen || (editDetail?.isOpen && editDetail?.contentLineIndex !== contentLineIndex)) &&
                                                                    <div>
                                                                        {contentLine?.text}
                                                                    </div>
                                                                }

                                                                {editDetail?.isOpen && editDetail?.contentLineIndex === contentLineIndex &&
                                                                    <div
                                                                        className={'w-full flex justify-between items-center'}>
                                                                        <textarea
                                                                            id="editDetailText"
                                                                            name="text"
                                                                            value={editDetail?.text}
                                                                            onChange={handleEditDetailChanges}
                                                                            className="w-full h-24 mr-2 border border-sky-500 p-4 rounded"/>
                                                                        <div
                                                                            className={'justify-center items-center'}>
                                                                            <div>
                                                                                <button
                                                                                    onClick={confirmEditDetailInfo}
                                                                                    className={`rounded flex items-center justify-center border button-outline-green m-1 w-20 px-2 py-1 text-sm`}>
                                                                                    Confirm
                                                                                </button>
                                                                            </div>
                                                                            <div>
                                                                                <button
                                                                                    onClick={cancelEditDetailInfo}
                                                                                    className={`rounded flex items-center justify-center border button-gray-outline m-1 w-20 px-2 py-1 text-sm`}>
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                }

                                                                {apiCallInProgressFor === contentLine?.text &&
                                                                    <div className={'ml-6'}>
                                                                        <ShowProgressSpinner
                                                                            sizeClass="w-6 h-6"
                                                                            dataFetchInProgress={apiCallInProgressFor === contentLine?.text}/>
                                                                    </div>
                                                                }
                                                            </div>

                                                            {contentLine.links && contentLine.links.length > 0 &&
                                                                <div className={'my-1'}>
                                                                    {contentLine.links.map((link, linkIndex) => (
                                                                        <div key={linkIndex}
                                                                             className={'flex justify-start items-center mb-1'}>
                                                                            <DeleteItem
                                                                                handleDeleteItem={() => {
                                                                                    handleDeleteLink(linkIndex, contentLineIndex);
                                                                                }}
                                                                                iconSize={'h-3 w-3'}
                                                                                itemIndex={linkIndex}
                                                                                showIcon={true}/>

                                                                            <div className={'ml-1'}>
                                                                                <a href={link.url}
                                                                                   className={'ml-2 pr-2 underline text-blue-500'}>{link.url}</a>
                                                                            </div>

                                                                            <div
                                                                                className={'ml-2 text-gray-500'}>{link.value}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            }

                                                            {!previewMode &&
                                                                <div
                                                                    className={`flex justify-between items-center my-1`}>
                                                                    <SelectHtmlTag
                                                                        showKey={false}
                                                                        contentLine={contentLine}
                                                                        contentLineIndex={contentLineIndex}
                                                                        changeContentLineType={changeContentLineType}/>

                                                                    <div
                                                                        className={'flex w-1/2 justify-end items-center'}>
                                                                        {(!editDetail?.isOpen || (editDetail?.isOpen && editDetail?.contentLineIndex !== contentLineIndex)) &&
                                                                            <div>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        openEditDetailInfo(contentLine, contentLineIndex);
                                                                                    }}
                                                                                    className={`rounded flex items-center justify-center border button-outline-green m-1 px-2 py-1 text-sm`}>
                                                                                    Edit
                                                                                </button>
                                                                            </div>
                                                                        }

                                                                        {contentLine.isSection &&
                                                                            <>
                                                                                <GenerateContentFromPrompt
                                                                                    label={'Expand'}
                                                                                    noIcon={true}
                                                                                    showCopyButton={false}
                                                                                    sizeClass={'p-1 text-sm'}
                                                                                    contentLineIndex={contentLineIndex}
                                                                                    afterContentGenerated={prepareExpandedSectionContentLines}
                                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                                    triggeredFor={contentLine?.text}
                                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.EXPAND_CONTENT.title}
                                                                                    promptContent={mainContentPrompt}
                                                                                    previousResponse={mainContent}
                                                                                    newPrompt={prepareExpandSectionContentPrompt(contentLine)}/>

                                                                                <div>
                                                                                    <button
                                                                                        onClick={() => triggerContentLinkMappingProcess(contentLine)}
                                                                                        className={`rounded flex items-center justify-center border button-outline-green m-1 px-2 py-1 text-sm`}>
                                                                                        Add Links
                                                                                    </button>
                                                                                </div>

                                                                                <GenerateContentFromPrompt
                                                                                    label={'Rewrite Title'}
                                                                                    noIcon={true}
                                                                                    showCopyButton={false}
                                                                                    sizeClass={'p-1 text-sm'}
                                                                                    contentLineIndex={contentLineIndex}
                                                                                    afterContentGenerated={prepareSectionTitles}
                                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                                    triggeredFor={contentLine?.text}
                                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.REWRITE_SECTION_TITLE.title}
                                                                                    promptContent={prepareRewriteSectionTitlePrompt(contentLine)}/>
                                                                            </>
                                                                        }

                                                                        {!contentLine.isSection && !contentLine.isImage &&
                                                                            <>
                                                                                {!contentLine?.isImage &&
                                                                                    <div className={'m-1'}>

                                                                                        <AddEditProductImage
                                                                                            afterProductImageSelected={(event) => {
                                                                                                addImageToContentLine(event, contentLineIndex);
                                                                                            }}
                                                                                            productsList={productsList}/>

                                                                                    </div>
                                                                                }

                                                                                <GenerateContentFromPrompt
                                                                                    label={'Add Links'}
                                                                                    noIcon={true}
                                                                                    showCopyButton={false}
                                                                                    sizeClass={'p-1 text-sm'}
                                                                                    contentLineIndex={contentLineIndex}
                                                                                    afterContentGenerated={prepareContentLinks}
                                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                                    triggeredFor={contentLine?.text}
                                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.EXTRACT_KEYWORDS_AND_MAP_LINKS.title}
                                                                                    promptContent={prepareExtractKeyWordsAndMapLinksPrompt(contentLine)}/>

                                                                                <GenerateContentFromPrompt
                                                                                    label={'Rewrite'}
                                                                                    noIcon={true}
                                                                                    showCopyButton={false}
                                                                                    sizeClass={'p-1 text-sm'}
                                                                                    contentLineIndex={contentLineIndex}
                                                                                    afterContentGenerated={prepareRewrittenContentLine}
                                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                                    triggeredFor={contentLine?.text}
                                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.REWRITE_CONTENT.title}
                                                                                    promptContent={prepareRewriteSectionContentPrompt(contentLine)}/>
                                                                            </>
                                                                        }

                                                                        <div className={'ml-1'}>
                                                                            <DeletePostContentLine
                                                                                contentLineIndex={contentLineIndex}
                                                                                handleRemoveContentLine={handleRemoveContentLine}
                                                                                showIcon={true}/>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </>

                                        }
                                    </div>
                                ))}
                            </>
                            
                        }

                    </div>

                    <div
                        className="ml-8 p-4 w-1/3 bg-gray-400 shadow-xl rounded-lg product-blog-post-content-container overflow-y-scroll">
                        <ProductBlogPostMeta
                            blogPostContent={blogPostContent}/>

                        <div className="mb-4">
                            <ProductBlogPostStatistics statistics={statistics}/>
                        </div>

                        <div className="mb-10">
                            <ProductBlogPostInstructions/>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProductBlogPostWriter

